import type { Locale } from '@croissants/i18n';
import { createLocaleContext, type KeyValueStore } from '@croissants/ui';
import type { JSX, ReactNode } from 'react';
import { type AppTranslate, appTextFor, platformTextFor } from '../i18n/index';
import { setRequestLocale } from '../lib/api';
import { LOCALE_STORAGE_KEY, localeStore } from '../lib/locale';

/**
 * The active locale, and the two translators every screen uses (PRD GST-006,
 * NFR-006).
 *
 * The storefront opens in the browser's language when it is one this build ships
 * and in English otherwise; a guest who uses the language switch overrides that
 * for good. The choice is also pushed onto the QR session by `SessionProvider`,
 * because it is what notifications are sent in (NTF-004) - the switch is not a
 * display setting, which is what `onChange` carries.
 *
 * The provider itself is `@croissants/ui`'s `createLocaleContext`, shared with
 * the three portals. What is the storefront's own is here: its two catalogues,
 * its store - which keeps an in-memory copy, because a QR code scanned inside a
 * camera app's webview lands in a browser that may have no storage at all - and
 * the request header, which follows the state rather than being attached at
 * each call site.
 */

export interface GuestTranslators {
  /** This app's own screen copy. */
  readonly t: AppTranslate;
  /** The shared platform catalogue: stages, prices, cancellation, tips. */
  readonly pt: ReturnType<typeof platformTextFor>;
}

const locale = createLocaleContext<GuestTranslators>({
  storageKey: LOCALE_STORAGE_KEY,
  defaultLocale: 'en',
  useBrowserLanguages: true,
  derive: (next) => ({ t: appTextFor(next), pt: platformTextFor(next) }),
  // The API answers in the guest's language too, so the header follows the
  // state rather than being attached at each call site.
  apply: (next) => setRequestLocale(next),
});

export type LocaleContextValue = ReturnType<typeof locale.useLocale>;

export const { useLocale, useT, initialLocale } = locale;

export interface LocaleProviderProps {
  readonly children: ReactNode;
  /** Called when the guest picks a language, so the session can persist it. */
  readonly onChange?: (locale: Locale) => void;
  /** Overridden only by a test that states what this device remembers. */
  readonly storage?: KeyValueStore;
}

/**
 * The storefront's provider, with its own store bound in - so the language a
 * guest chose can never be read from one place and written to another.
 */
export function LocaleProvider({ children, onChange, storage }: LocaleProviderProps): JSX.Element {
  return (
    <locale.LocaleProvider storage={storage ?? localeStore} onChange={onChange}>
      {children}
    </locale.LocaleProvider>
  );
}
